import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTable, useGlobalFilter, useSortBy } from 'react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretUp, faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { abbreviateNumber } from '../utils/abbreviateNumber';
import GlobalFilter from './GlobalFilter';
import SmallLogo from './SmallLogo';

const Table = () => {
  const richLists = useSelector((state) => state.richLists);
  const { lists, activeList } = richLists;

  let [currentFullList] = lists.filter((x) => x[0].list === activeList);

  const data = useMemo(() => currentFullList, [currentFullList]);

  const columns = useMemo(
    () => [
      {
        Header: '',
        width: '20',
        id: 'emoji',
        accessor: (x) => {
          return x.rank === 1
            ? '🏆'
            : x.rank === 2
            ? '🥈'
            : x.rank === 3
            ? '🥉'
            : '';
        },
        disableSortBy: true,
      },
      {
        Header: '',
        accessor: 'rank',
      },
      {
        Header: 'Name',
        accessor: 'name',
      },
      {
        Header: 'Role',
        accessor: 'role',
        Cell: (props) => {
          return (
            <>{!props.value ? <span>-</span> : <span>{props.value}</span>}</>
          );
        },
      },
      {
        Header: 'Sector',
        accessor: 'sector',
      },
      {
        Header: 'Net Worth',
        accessor: 'value',
        Cell: (props) => {
          return <>${abbreviateNumber(props.value)}</>;
        },
        sortType: 'basic',
      },
      {
        Header: 'YoY % Change',
        accessor: 'valuechange',
        Cell: (props) => {
          return (
            <>
              {props.value > 0 ? (
                <span className="text-green-500"> {props.value}%</span>
              ) : props.value < 0 ? (
                <span className="text-red-500"> {props.value}%</span>
              ) : (
                <span> {props.value || 0}</span>
              )}
            </>
          );
        },
        sortType: 'basic',
      },
    ],
    []
  );

  const tableInstance = useTable(
    {
      columns,
      data,
    },
    useGlobalFilter,
    useSortBy
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    setGlobalFilter,
  } = tableInstance;

  const { globalFilter } = state;

  return (
    <>
      <GlobalFilter
        filter={globalFilter}
        setFilter={setGlobalFilter}
        rows={rows}
      />
      <table
        {...getTableProps()}
        className="border-separate max-w-screen w-full text-left font-mono text-sm"
        style={{ borderSpacing: '0 0.5rem' }}
      >
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, index) => {
                return index === 0 || index === 3 || index === 4 ? (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className="hidden md:table-cell bg-gray-200 p-3 whitespace-nowrap"
                  >
                    {index === 0 ? <SmallLogo /> : column.render('Header')}
                    <span>
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <FontAwesomeIcon className="ml-2" icon={faCaretUp} />
                        ) : (
                          <FontAwesomeIcon
                            className="ml-2"
                            icon={faCaretDown}
                          />
                        )
                      ) : (
                        ''
                      )}
                    </span>
                  </th>
                ) : (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className="bg-gray-200 text-left text-xs md:text-sm p-3"
                  >
                    {index === 1 ? (
                      <span className="tabel-cell md:hidden">
                        <SmallLogo />
                      </span>
                    ) : (
                      column.render('Header')
                    )}
                    <span>
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <FontAwesomeIcon className="ml-2" icon={faCaretUp} />
                        ) : (
                          <FontAwesomeIcon
                            className="ml-2"
                            icon={faCaretDown}
                          />
                        )
                      ) : (
                        ''
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()} className="my-2">
                {row.cells.map((cell, index) => {
                  return index === 0 || index === 3 || index === 4 ? (
                    <td
                      {...cell.getCellProps()}
                      className="hidden md:table-cell text-xs bg-white p-3"
                    >
                      {cell.render('Cell')}
                    </td>
                  ) : (
                    <td {...cell.getCellProps()} className="bg-white p-3">
                      {cell.render('Cell')}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
};

export default Table;
